import axios from 'axios';
import router from '@/router';
import Cookies from 'js-cookie';

// 创建 axios 实例
const request = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 添加跳转状态标记，防止重复跳转
let isRedirecting = false;

// 清除认证信息的统一方法
const clearAuthInfo = () => {
  localStorage.clear();
  Cookies.remove('satoken');
};

// 跳转到登录页的统一方法
const redirectToLogin = () => {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login' && !isRedirecting) {
    isRedirecting = true;
    router.push('/login');
    // 重置跳转标记，避免永久锁定
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }
};

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 添加 token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 显示加载状态
    if (config.showLoading !== false) {
      console.log('请求开始:', config.url);
    }

    return config;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const { data, config } = response;
    
    // 隐藏加载状态
    if (config.showLoading !== false) {
      console.log('请求完成:', config.url);
    }

    // 处理业务错误
    if (data.code !== 200 && data.code !== 0) {
      let errorMessage = data.message || '请求失败';
      
      // 根据状态码设置错误信息
      switch (data.code) {
        case 200:
          if (data.success === false) {
            errorMessage = '参数校验错误';
          }
          break;
        case 401: 
        case 408:
          console.log('认证失败，准备跳转登录页');
          errorMessage = 'token 失效或者未登录';
          // 清除认证信息并跳转到登录页
          clearAuthInfo();
          redirectToLogin();
          break;
        case 403:
          errorMessage = '无权限';
          break;
        case 10001:
          errorMessage = '用户不存在';
          break;
        case 10002:
          errorMessage = '用户已禁用';
          break;
        case 10003:
          errorMessage = '仅支持单电';
          break;
        case 10004:
          errorMessage = '未绑定电池';
          break;
        case 10005:
          errorMessage = '余额不足';
          break;
        case 10006:
          errorMessage = '设备不存在';
          break;
        case 10007:
          errorMessage = '距离太远';
          break;
        case 10008:
          errorMessage = '未实名认证';
          break;
        case 10009:
          errorMessage = '失败';
          break;
        case 100010:
          errorMessage = '设备离线';
          break;
        case 100011:
          errorMessage = '门店无商户';
          break;
        case 100012:
          errorMessage = '单价比例未设置';
          break;
        case 100013:
          errorMessage = '已欠费';
          break;
        case 100014:
          errorMessage = '未绑定门店';
          break;
        case 100015:
          errorMessage = '门店未设置负责人';
          break;
        case 100016:
          errorMessage = '设备已禁用';
          break;
        case 100017:
          errorMessage = '验证码错误';
          break;
        case 100018:
          errorMessage = '密码错误';
          break;
        case 100019:
          errorMessage = '协议不支持';
          break;
        case 100020:
          errorMessage = '超出范围';
          break;
        default:
          errorMessage = data.message || '请求失败';
      }
      
      // 显示错误信息
      if (config.showError !== false) {
        console.error('业务错误:', errorMessage);
        // 这里可以添加全局错误提示
      }

      return Promise.reject(new Error(errorMessage));
    }

    return response;
  },
  (error) => {
    console.error('响应错误:', error);
    
    let errorMessage = '网络错误';
    
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
        case 408:
          errorMessage = '认证失败，请重新登录';
          clearAuthInfo();
          redirectToLogin();
          break;
        case 403:
          errorMessage = '拒绝访问';
          break;
        case 404:
          errorMessage = '请求地址出错';
          break;
        case 500:
          errorMessage = '服务器内部错误';
          break;
        default:
          errorMessage = data?.message || `请求失败: ${status}`;
      }
    } else if (error.request) {
      errorMessage = '网络连接失败';
    } else {
      errorMessage = error.message || '请求配置错误';
    }

    // 显示错误信息
    if (error.config && error.config.showError !== false) {
      console.error('请求错误:', errorMessage);
    }

    return Promise.reject(new Error(errorMessage));
  }
);

// 封装请求方法
export const http = {
  get: (url, config = {}) => {
    return request.get(url, config).then(response => response.data);
  },

  post: (url, data, config = {}) => {
    return request.post(url, data, config).then(response => response.data);
  },

  put: (url, data, config = {}) => {
    return request.put(url, data, config).then(response => response.data);
  },

  delete: (url, config = {}) => {
    return request.delete(url, config).then(response => response.data);
  },

  patch: (url, data, config = {}) => {
    return request.patch(url, data, config).then(response => response.data);
  },
};

// 导出 axios 实例（用于特殊需求）
export default request; 